import { paths } from './paths';

// ----------------------------------------------------------------------

/**
 * Maps pathname prefixes (same string values as `paths`) to short section labels.
 * Resolution uses longest-prefix match on normalized pathname.
 */
export const PATH_TITLE_MAP: Record<string, string> = {
  [paths.dashboard.root]: 'Dashboard',

  [paths.gettingStarted.root]: 'Getting started',

  [paths.jobs.root]: 'Job Board',
  [paths.jobs.jobDetails]: 'Job details',
  [paths.jobs.editorSample]: 'Editor sample',
  [paths.jobs.dashboard]: 'Job dashboard',
  [paths.jobs.candidates]: 'Candidates',
  [paths.jobs.workflow]: 'Workflow',
  [paths.jobs.reports]: 'Job reports',
  [paths.jobs.createJob]: 'Create job',
  [paths.jobs.interviewHub]: 'Interview hub',
  [paths.jobs.interviewAgenda]: 'Interview agenda',
  [paths.jobs.interviewEvaluation]: 'Interview evaluation',
  [paths.jobs.compare]: 'Compare',

  [paths.createJob.root]: 'Create job',

  [paths.candidateEvidenceView.root]: 'Resume',

  [paths.candidates.root]: 'Candidates',
  '/candidates/details': 'Candidate Detail',
  '/candidates/new-details': 'Candidate Detail',

  [paths.orgSettings.root]: 'Organization',
  [paths.orgSettings.info]: 'Company info',
  [paths.orgSettings.billing]: 'Billing & plans',
  [paths.orgSettings.members]: 'Members',
  [paths.orgSettings.invitations]: 'Invitations',
  [paths.orgSettings.knowledgeBase]: 'Knowledge base',
  [paths.orgSettings.aiPersona]: 'AI persona',
  [paths.orgSettings.notifications.emailTemplates]: 'Email templates',
  [paths.orgSettings.notifications.root]: 'Notifications',

  [`${paths.orgSettings.billing}/success`]: 'Billing success',
  [`${paths.orgSettings.billing}/portal-return`]: 'Billing portal',

  [paths.billing.success]: 'Billing success',
  [paths.billing.cancel]: 'Billing canceled',
  [paths.billing.portalReturn]: 'Billing portal',

  [paths.eventsTrial]: 'Events',

  [paths.auth.auth0.signIn]: 'Sign in',
  [paths.auth.auth0.verifyEmail]: 'Verify email',
  [paths.auth.auth0.onBoarding]: 'Onboarding',
  '/auth/callback': 'Signing in',

  '/accept-invitation': 'Invitation',

  [paths.newCandidates.root]: 'Candidates',
  [paths.newCandidates.candidateDetails]: 'Candidate',

  // Future / optional routes from paths (not all are mounted today)
  [paths.auth.jwt.signIn]: 'Sign in',
  [paths.auth.jwt.signUp]: 'Sign up',
};

/** Descending length — longest prefix wins */
const SORTED_PATH_KEYS = Object.keys(PATH_TITLE_MAP).sort((a, b) => b.length - a.length);

// ----------------------------------------------------------------------

export function normalizePathname(pathname: string): string {
  if (!pathname) {
    return '/';
  }
  let p = pathname.trim().toLowerCase();
  if (p !== '/') {
    p = p.replace(/\/+$/, '');
  }
  return p || '/';
}

export function getPageTitleSegment(pathname: string): string | undefined {
  const norm = normalizePathname(pathname);

  for (const key of SORTED_PATH_KEYS) {
    const prefix = normalizePathname(key);
    if (norm === prefix || (prefix !== '/' && norm.startsWith(`${prefix}/`))) {
      return PATH_TITLE_MAP[key];
    }
  }

  return undefined;
}

export function formatPageTitle(segment: string, appName: string): string {
  return `${segment} - ${appName}`;
}

export function getPageTitle(pathname: string, appName: string, fallbackSegment = 'Page'): string {
  const segment = getPageTitleSegment(pathname) ?? fallbackSegment;
  return formatPageTitle(segment, appName);
}
